import React from "react";

export type Co2TrackedLoaderVariant = "inline" | "contained" | "overlay";

export type Co2TrackedLoaderProps = {
    variant?: Co2TrackedLoaderVariant;
    height?: number | string;
    minHeight?: number | string;
    title?: string;
    message?: string;
    className?: string;
    style?: React.CSSProperties;
    "aria-label"?: string;
};

const LOGO_COLOR = "rgb(10, 179, 156)";

const Co2TrackedLoaderVisual = () => (
    <div className="co2-tracked-loader__visual" aria-hidden="true">
        <div className="co2-tracked-loader__orbit-track" />

        <div className="co2-tracked-loader__center-logo">
            <svg viewBox="0 0 181 113" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path
                    fillRule="evenodd"
                    clipRule="evenodd"
                    d="M125.13 52.88C125.13 52.88 125.12 52.88 125.04 52.93C124.52 53.32 121.72 55.39 113.34 56.71C113.51 56.74 112.03 56.93 110.17 57.16C107.57 57.49 104.23 57.91 103.59 58.14C95.3901 61.1 94.3301 70.29 97.5401 75.54C97.6201 75.44 97.6901 75.33 97.7701 75.23C98.9401 73.76 100.56 72.14 102.22 70.79C103.69 69.59 104.95 68.8 105.56 68.42L106.16 68.04L107.55 70.26L106.95 70.64C106.34 71.02 105.22 71.72 103.87 72.82C102.35 74.06 100.87 75.53 99.8101 76.86C98.8001 78.13 97.9701 79.51 97.1901 81.21C96.5301 82.66 96.0401 84.05 95.8001 84.72L95.6201 85.22L98.5201 86.25L98.7701 85.54C99.0201 84.84 99.4901 83.52 100.12 82.14C100.59 81.12 101.13 80.09 101.82 79.08C106.09 80.68 112.08 79.48 117.25 75.55C119.46 73.87 121.16 71.21 122.44 68.35L123.04 66.92C123.85 64.84 124.52 62.64 125.06 60.32L125.24 59.52C125.87 56.63 125.11 52.89 125.11 52.89L125.13 52.88Z"
                    fill={LOGO_COLOR}
                />
                <path
                    d="M43.36 82.915C57.56 82.455 69.04 70.765 69.04 56.505V56.425C69.03 34.125 87.04 15.805 109.39 15.395C131.72 15.805 149.75 34.075 149.75 56.495C149.75 78.915 131.72 97.195 109.39 97.595C99.66 97.415 90.74 93.845 83.78 88.015C87.06 86.765 93.6 82.005 94.78 78.405C98.8 81.125 104.23 82.745 109.39 82.915C123.59 82.455 135.06 70.755 135.06 56.505C135.06 42.255 123.59 30.555 109.39 30.095C95.19 30.555 83.72 42.255 83.72 56.505C83.72 78.835 65.76 97.205 43.36 97.605C21.03 97.195 3 78.925 3 56.495C3 34.065 21.03 15.805 43.36 15.395C53.09 15.565 62.01 19.145 68.97 24.975C65.9 28.965 63.4 33.405 61.59 38.185C56.94 33.385 50.49 30.315 43.36 30.075C29.15 30.535 17.69 42.235 17.69 56.485C17.69 70.735 29.16 82.445 43.36 82.895V82.915Z"
                    fill={LOGO_COLOR}
                />
                <path
                    d="M154.1 97.445V91.645L165.18 82.485C165.91 81.875 166.54 81.305 167.06 80.775C167.59 80.235 168 79.675 168.28 79.105C168.57 78.535 168.71 77.905 168.71 77.205C168.71 76.445 168.55 75.785 168.22 75.245C167.9 74.705 167.46 74.285 166.89 73.995C166.32 73.695 165.66 73.545 164.92 73.545C164.18 73.545 163.52 73.695 162.95 73.995C162.39 74.295 161.95 74.735 161.65 75.315C161.34 75.895 161.19 76.605 161.19 77.435H153.53C153.53 75.345 154 73.545 154.94 72.045C155.88 70.535 157.2 69.375 158.91 68.565C160.62 67.755 162.63 67.345 164.93 67.345C167.23 67.345 169.37 67.725 171.11 68.495C172.86 69.245 174.21 70.315 175.16 71.685C176.12 73.055 176.6 74.665 176.6 76.515C176.6 77.655 176.36 78.795 175.89 79.925C175.42 81.045 174.57 82.285 173.34 83.655C172.11 85.015 170.37 86.645 168.1 88.525L165.32 90.845V91.015H176.92V97.455H154.13L154.1 97.445Z"
                    fill={LOGO_COLOR}
                />
            </svg>
        </div>

        <div className="co2-tracked-loader__orbiter">
            <div className="co2-tracked-loader__moving-arrow">
                <svg viewBox="0 0 32 20" xmlns="http://www.w3.org/2000/svg">
                    <path className="co2-tracked-loader__arrow-line" d="M2 10H22" />
                    <path className="co2-tracked-loader__arrow-head" d="M19 0L31 10L19 20Z" />
                </svg>
            </div>
        </div>
    </div>
);

const Co2TrackedLoader = ({
    variant = "inline",
    height,
    minHeight,
    title,
    message,
    className = "",
    style,
    "aria-label": ariaLabel = "Loading",
}: Co2TrackedLoaderProps) => {
    const variantClass = `co2-tracked-loader--${variant}`;
    const containerStyle: React.CSSProperties = {
        ...style,
        ...(height !== undefined ? { height: typeof height === "number" ? `${height}px` : height } : {}),
        ...(minHeight !== undefined ? { minHeight: typeof minHeight === "number" ? `${minHeight}px` : minHeight } : {}),
    };

    return (
        <div
            className={`co2-tracked-loader ${variantClass} ${className}`.trim()}
            style={containerStyle}
            role="status"
            aria-live="polite"
            aria-label={ariaLabel}
        >
            <div className="co2-tracked-loader__content">
                <Co2TrackedLoaderVisual />

                {title ? <p className="co2-tracked-loader__title">{title}</p> : null}

                {message ? (
                    <p className="co2-tracked-loader__message">
                        {message}
                        <span className="co2-tracked-loader__dots" aria-hidden="true">
                            <span>.</span>
                            <span>.</span>
                            <span>.</span>
                        </span>
                    </p>
                ) : null}
            </div>
        </div>
    );
};

export default Co2TrackedLoader;
